/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { FormField } from "components/actions/forms/FormField";
import { LoadingSpinner } from "components/loading-spinner/LoadingSpinner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { ActionType, CellProps, Column, Row, TableInstance, TableState } from "react-table";
import { sandboxApi } from "services/sandbox-api/api";
import { OptionalFileSet } from "types";
import { DefaultCell, SelectionColumn, Table, Tag, TagContainer, Text, Tooltip } from "@itwin/itwinui-react";
import { SourceFile } from "@sandbox/code-editor";
import "./OptionalFilesTable.scss";

/**
 * Interface to represent an optional fileset with a property that determines whether it's selected
 */
export interface FileSetSelectionOption extends Record<string, any> {
  displayName: string;
  name: string;
  version: string;
  selected: boolean;
  files?: SourceFile[];
  dependencies?: string[];
  description?: string;
}

/**
 * Props passed into this component
 */
export interface OptionalFilesTableProps extends FormField {
  selectedOptionalFiles?: OptionalFileSet[];
  saveButtonDisabled?: boolean;
}

const transformToSelection = (selected?: OptionalFileSet[]): FileSetSelectionOption[] => {
  return selected?.map((fileSet) => {
    return {
      ...fileSet,
      displayName: fileSet.displayName || fileSet.name,
      selected: true,
    };
  }) || [];
};

/**
 * Component for creating a table of verbose descriptions of optional/helper files
 */
export const OptionalFilesTable = ({ name, control }: OptionalFilesTableProps) => {
  const { data: optionalFiles, isLoading } = sandboxApi.useOptionalFilesQuery({ includeBody: true });
  const [selectedFiles, setSelectedFiles] = useState<FileSetSelectionOption[]>([]);
  const tableInstance = useRef<TableInstance<FileSetSelectionOption>>();
  const { field } = useController({ name, control });

  useEffect(() => {
    setSelectedFiles(transformToSelection(field.value))
  }, [field.value]);

  const data = useMemo(() => {
    if (!selectedFiles)
      return [];

    return optionalFiles?.map((fileSet) => {
      return {
        ...fileSet,
        displayName: fileSet.displayName || fileSet.name,
        selected: selectedFiles.find((item) => item.name === fileSet.name && item.version === fileSet.version) !== undefined
      };
    }) || [];
  }, [selectedFiles, optionalFiles]);

  // Returns the dependents of a given row
  const getDependents = useCallback(
    (rowData) =>
      selectedFiles.filter((f) =>
        f.version === rowData.version && f.dependencies?.some((name) => name === rowData.name)
      ),
    [selectedFiles]
  );

  const getDependencyDisplayName = useCallback((name: string, version: string) => {
    const fileSet = optionalFiles?.find((item) => item.name === name && item.version === version);
    return fileSet?.displayName || fileSet?.name;
  }, [optionalFiles]);

  // Checkbox column for each row. The row will be disabled if required by any selected row
  const selectionColumn = SelectionColumn<FileSetSelectionOption>({
    isDisabled: (rowData) => !!getDependents(rowData).length || selectedFiles.find((item) => item.name === rowData.name && item.version !== rowData.version) !== undefined,
  });

  const columns = useMemo(
    (): Column<FileSetSelectionOption>[] => [
      {
        Header: "Table",
        columns: [
          {
            ...selectionColumn,
            Cell: (props: CellProps<FileSetSelectionOption>) => {
              const otherVersionSelected = selectedFiles.find((item) => item.name === props.row.original.name && item.version !== props.row.original.version) !== undefined;
              const dependentFiles = getDependents(props.row.original);

              if (!!dependentFiles.length)
                return (
                  <Tooltip
                    content={`This file selection is disabled because ${dependentFiles
                      .map((f) => `"${f.displayName}"`)
                      .join(", ")} depend(s) on it`}
                  >
                    <div className="disabled-selection-column">{selectionColumn.Cell(props)}</div>
                  </Tooltip>
                )

              if (otherVersionSelected)
                return (
                  <Tooltip content="Other version of this helper file is selected">
                    <div className="disabled-selection-column">{selectionColumn.Cell(props)}</div>
                  </Tooltip>
                )

              return selectionColumn.Cell(props);
            },
          },
          {
            id: "displayName",
            Header: "Name",
            accessor: "displayName",
          },
          {
            id: "version",
            Header: "Version",
            accessor: "version",
            width: 80,
            cellRenderer: (props) => {
              return <DefaultCell {...props} className="iui-text-muted" />;
            }
          },
        ],
      },
    ],
    [selectionColumn, getDependents, selectedFiles]
  );

  const getRowId = useCallback((rowData: FileSetSelectionOption) => `${rowData.name}@${rowData.version}`, []);

  const handleRowSelected = useCallback(
    (
      selectedData?: FileSetSelectionOption[],
      state?: TableState<FileSetSelectionOption>
    ) => {
      setSelectedFiles(selectedData ?? []);

      const selectedWithDependencies = [...(selectedData || [])];
      selectedData?.forEach((file) => {
        file.dependencies?.forEach((depFile) => {
          const rowId = `${depFile}@${file.version}`;
          const depRow = tableInstance.current?.rowsById[rowId];
          if (!state?.selectedRowIds[rowId] && depRow) {
            depRow.toggleRowSelected(true);
            selectedWithDependencies.push(depRow.original);
          }
        });
      });

      field.onChange(selectedWithDependencies);
    }, [field]);

  const handleRowClicked = useCallback(
    (event, row) => {
      // Prevents unselection if the row is required by another selected row
      if (getDependents(row.original)) {
        event.preventDefault();
      }
    },
    [getDependents, selectedFiles]
  );

  const expandedSubComponent = useCallback(
    (row: Row<FileSetSelectionOption>) => (
      <div className="optional-file-tree-sub-component">
        {row.original.description && <Text className="optional-file-description">{row.original.description}</Text>}
        <Text className="optional-file-leading">Files</Text>
        <TagContainer>
          {row.original.files?.map((file) => <Tag key={file.filePath} variant="basic">{file.filePath}</Tag>)}
        </TagContainer>
        {row.original.dependencies && row.original.dependencies.length > 0 && <>
          <Text className="optional-file-leading">Dependencies</Text>
          <TagContainer>
            {
              row.original.dependencies?.map((name) => <Tag key={name} variant="basic">{getDependencyDisplayName(name, row.original.version)}</Tag>)
            }
          </TagContainer>
        </>}
      </div>
    ), []);

  const tableStateReducer = (
    newState: TableState<FileSetSelectionOption>,
    _action: ActionType,
    _previousState: TableState<FileSetSelectionOption>,
    instance?: TableInstance<FileSetSelectionOption>
  ): TableState => {
    tableInstance.current = instance;
    return newState;
  };

  // Selects rows off of the initial data
  const initialState = useMemo(
    () => ({
      selectedRowIds: Object.fromEntries(
        data.map((file) => [`${file.name}@${file.version}`, !!file.selected])
      ),
    }),
    [data]
  );

  return <div className="optional-files-container">
    {isLoading
      ? <LoadingSpinner centered message="Getting helper files..." />
      : <>
        <Text className="description">Choose helper file sets to include in your sandbox. Helper files are code snippets that make getting started easier.</Text>
        <div className="optional-files-table">
          <Table
            emptyTableContent="No data."
            density="extra-condensed"
            styleType="zebra-rows"
            data={data}
            columns={columns}
            getRowId={getRowId}
            isSelectable={true}
            selectRowOnClick={false}
            onSelect={handleRowSelected}
            onRowClick={handleRowClicked}
            subComponent={expandedSubComponent}
            stateReducer={tableStateReducer}
            initialState={initialState}
          />
        </div>
      </>}
  </div>;
};
